import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  Divider,
  Paper,
  Stack,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Link,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import { useNavigate } from "react-router-dom";

const BRAND = "#003366";
const BRAND_HOVER = "#00264d";

const Register = () => {
  const navigate = useNavigate();
  const ciRef = useRef(null);

  const [csrfToken, setCsrfToken] = useState("");
  const [aceptaPoliticas, setAceptaPoliticas] = useState(false);

  const [ci, setCi] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [existe, setExiste] = useState(null);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [form, setForm] = useState({
    razon_social: "",
    nombre_comercial: "",
    ruc: "",
    direccion: "",
    telefono: "",
    email: ""
  });

  const [enviando, setEnviando] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [registroCompletado, setRegistroCompletado] = useState(false);
  const [correoConfirmado, setCorreoConfirmado] = useState("");

  const soloCorreoEditable = existe === true;

  const canVerify = useMemo(
    () => ci.trim().length === 10 && !buscando && !enviando && !registroCompletado,
    [ci, buscando, enviando, registroCompletado]
  );

  const canSubmit = useMemo(
    () => existe !== null && !enviando && !registroCompletado,
    [existe, enviando, registroCompletado]
  );

  useEffect(() => {
    document.title = "Hydro Luz | Registro";
    window.scrollTo(0, 0);
    obtenerCsrfToken();
  }, []);

  const obtenerCsrfToken = async () => {
    try {
      const res = await fetch("/sistema/get-csrf-token/", {
        method: "GET",
        credentials: "include"
      });
      const data = await res.json();
      setCsrfToken(data.csrfToken || "");
    } catch {
      setCsrfToken("");
    }
  };

  const limpiarMensajes = () => {
    setError("");
    setInfo("");
  };

  const resetFormulario = () => {
    setForm({
      razon_social: "",
      nombre_comercial: "",
      ruc: "",
      direccion: "",
      telefono: "",
      email: ""
    });
  };

  const resetTodo = () => {
    setCi("");
    setExiste(null);
    resetFormulario();
    setAceptaPoliticas(false);
    setBuscando(false);
    setEnviando(false);
    setRegistroCompletado(false);
    setCorreoConfirmado("");
    limpiarMensajes();
    setTimeout(() => {
      if (ciRef.current) ciRef.current.focus();
    }, 100);
  };

  const onChangeForm = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onChangeCI = (value) => {
    if (registroCompletado) return;
    const clean = value.replace(/\D/g, "").slice(0, 10);
    setCi(clean);
    limpiarMensajes();
    if (existe !== null) {
      setExiste(null);
      resetFormulario();
    }
  };

  const verificarCI = async () => {
    limpiarMensajes();

    const cedula = ci.trim();
    if (!cedula) {
      setError("Ingresa tu cédula para verificar si ya tienes compras registradas.");
      return;
    }
    if (cedula.length !== 10) {
      setError("La cédula debe tener 10 dígitos.");
      return;
    }

    setBuscando(true);
    setExiste(null);
    resetFormulario();

    try {
      const res = await fetch(`/sistema/buscar-persona-por-ci/?ci=${cedula}`, {
        method: "GET",
        credentials: "include"
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data?.error || "No se pudo verificar tu cédula. Inténtalo nuevamente.");
        return;
      }

      if (data.existe) {
        setExiste(true);
        setInfo(
          "Encontramos tu registro. Confirma tu correo para enviarte la clave temporal y habilitar tu acceso a puntos y beneficios."
        );

        const p = data.persona || {};
        setForm({
          razon_social: p.razon_social || "",
          nombre_comercial: p.nombre_comercial || "",
          ruc: p.ruc || "",
          direccion: p.direccion || "",
          telefono: p.telefono || "",
          email: p.correo || ""
        });
      } else {
        setExiste(false);
        setInfo(
          "Aún no encontramos compras asociadas a tu cédula. Completa tus datos para crear tu cuenta y empezar a acumular puntos en tus próximas compras."
        );
        resetFormulario();
      }
    } catch {
      setError("No se pudo conectar con el servidor. Inténtalo nuevamente.");
    } finally {
      setBuscando(false);
    }
  };

  const registrar = async (e) => {
    e.preventDefault();
    limpiarMensajes();

    const cedula = ci.trim();
    if (!cedula || cedula.length !== 10) {
      setError("Verifica tu cédula antes de continuar.");
      return;
    }
    if (existe === null) {
      setError("Primero verifica tu cédula.");
      return;
    }

    const email = form.email.trim().toLowerCase();
    if (!email) {
      setError("El correo es obligatorio para enviarte la clave temporal.");
      return;
    }

    const ruc = form.ruc.trim();
    if (ruc && (!/^\d{13}$/.test(ruc))) {
      setError("El RUC debe tener 13 dígitos.");
      return;
    }

    if (existe === false) {
      if (!form.razon_social.trim()) {
        setError("El nombre (Razón social) es obligatorio para tu facturación.");
        return;
      }
      if (!form.telefono.trim()) {
        setError("El teléfono es obligatorio.");
        return;
      }
    }

    if (!aceptaPoliticas) {
      setError("Debes aceptar las políticas de privacidad para continuar.");
      return;
    }

    if (!csrfToken) {
      setError("No se pudo obtener el token CSRF. Recarga la página e intenta nuevamente.");
      return;
    }

    setEnviando(true);

    try {
      const razonSocial = form.razon_social.trim();
      const nombreComercial = (form.nombre_comercial.trim() || razonSocial).trim();

      const payload = {
        cedula: cedula,
        email: email,
        nombre: razonSocial || nombreComercial || "CLIENTE",
        telefono: form.telefono.trim(),
        razon_social: razonSocial,
        nombre_comercial: nombreComercial,
        ruc: ruc,
        direccion: form.direccion.trim()
      };

      const res = await fetch("/sistema/registrar-usuario-cliente/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken
        },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        setCorreoConfirmado(email);
        setRegistroCompletado(true);
        setSuccessOpen(true);
      } else {
        setError(
          data?.error ||
            "No se pudo completar el registro. Verifica tus datos e intenta nuevamente."
        );
      }
    } catch {
      setError("Error de conexión con el servidor.");
    } finally {
      setEnviando(false);
    }
  };

  const cerrarModalYLimpiar = () => {
    setSuccessOpen(false);
    resetTodo();
  };

  const irLogin = () => {
    setSuccessOpen(false);
    resetTodo();
    navigate("/login");
  };

  const readOnlySx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#f5f7fa" }
  };

  const disabledOverlay = registroCompletado
    ? { opacity: 0.65, pointerEvents: "none", filter: "grayscale(10%)" }
    : {};

  return (
    <Box sx={{ bgcolor: "#F7F9FC", minHeight: "100vh", py: { xs: 4, sm: 6 } }}>
      <Container maxWidth="sm">
        <Box textAlign="center" mb={3}>
          <Typography
            sx={{
              fontSize: { xs: "1.7rem", sm: "2.2rem" },
              fontWeight: 900,
              color: BRAND,
              letterSpacing: "-0.02em"
            }}
          >
            Activa tus beneficios
          </Typography>

          <Typography sx={{ mt: 1, color: "#334155", lineHeight: 1.7 }}>
            Verifica tu cédula para acceder a tu <strong>historial de compras</strong>,{" "}
            <strong>puntos acumulados</strong> y <strong>promociones exclusivas</strong>.
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
          >
            <Chip size="small" icon={<BadgeRoundedIcon />} label="Puntos por compras" />
            <Chip size="small" label="Beneficios exclusivos" />
            <Chip size="small" label="Promociones personalizadas" />
          </Stack>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #E6EDF7",
            p: { xs: 2, sm: 3 },
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)"
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {info && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {info}
            </Alert>
          )}

          <Box sx={disabledOverlay}>
            <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}>
              1) Verifica tu cédula
            </Typography>

            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Cédula"
                  fullWidth
                  value={ci}
                  inputRef={ciRef}
                  onChange={(e) => onChangeCI(e.target.value)}
                  inputProps={{ inputMode: "numeric", maxLength: 10 }}
                  helperText="Solo números. Ej: 0102030405"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeRoundedIcon sx={{ color: BRAND }} />
                      </InputAdornment>
                    ),
                    endAdornment: ci ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Limpiar"
                          onClick={() => onChangeCI("")}
                          edge="end"
                          disabled={registroCompletado}
                        >
                          <ClearRoundedIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  onClick={verificarCI}
                  disabled={!canVerify}
                  startIcon={!buscando ? <SearchRoundedIcon /> : null}
                  sx={{
                    height: 56,
                    borderRadius: 3,
                    fontWeight: 900,
                    letterSpacing: "0.02em",
                    textTransform: "none",
                    color: "#fff",
                    backgroundColor: BRAND,
                    boxShadow: "0 10px 20px rgba(0,51,102,0.25)",
                    "&:hover": {
                      backgroundColor: BRAND_HOVER,
                      boxShadow: "0 12px 24px rgba(0,51,102,0.30)"
                    },
                    "&:disabled": {
                      backgroundColor: "#A8B7C6",
                      color: "#F7FAFC"
                    }
                  }}
                >
                  {buscando ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                      <span>Verificando</span>
                    </Stack>
                  ) : (
                    "Verificar"
                  )}
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            {existe !== null && (
              <Box component="form" onSubmit={registrar}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>
                    2) Completa tu cuenta
                  </Typography>
                  <Chip
                    size="small"
                    sx={{
                      fontWeight: 800,
                      color: existe ? "#0f5132" : "#7a4b00",
                      backgroundColor: existe ? "#d1e7dd" : "#fff3cd",
                      borderRadius: 2
                    }}
                    label={existe ? "Cliente encontrado" : "Nuevo registro"}
                  />
                </Stack>

                <Typography variant="body2" sx={{ color: "#475569", mb: 2, lineHeight: 1.6 }}>
                  {existe
                    ? "Tus datos ya están registrados. Solo confirma tu correo para enviarte la clave temporal."
                    : "Estos datos son importantes para tu facturación y para activar tu cuenta de beneficios."}
                </Typography>

                <TextField
                  label="Razón social (Nombre completo)"
                  name="razon_social"
                  fullWidth
                  margin="normal"
                  value={form.razon_social}
                  onChange={onChangeForm}
                  InputProps={{
                    readOnly: soloCorreoEditable,
                    endAdornment: soloCorreoEditable ? (
                      <InputAdornment position="end">
                        <Tooltip title="Dato registrado">
                          <LockRoundedIcon sx={{ color: "#94a3b8" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null
                  }}
                  sx={soloCorreoEditable ? readOnlySx : undefined}
                />

                <TextField
                  label="Nombre comercial (Opcional)"
                  name="nombre_comercial"
                  fullWidth
                  margin="normal"
                  value={form.nombre_comercial}
                  onChange={onChangeForm}
                  InputProps={{
                    readOnly: soloCorreoEditable,
                    endAdornment: soloCorreoEditable ? (
                      <InputAdornment position="end">
                        <Tooltip title="Dato registrado">
                          <LockRoundedIcon sx={{ color: "#94a3b8" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null
                  }}
                  sx={soloCorreoEditable ? readOnlySx : undefined}
                />

                <TextField
                  label="RUC (Opcional)"
                  name="ruc"
                  fullWidth
                  margin="normal"
                  value={form.ruc}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 13);
                    setForm((p) => ({ ...p, ruc: v }));
                  }}
                  InputProps={{
                    readOnly: soloCorreoEditable,
                    endAdornment: soloCorreoEditable ? (
                      <InputAdornment position="end">
                        <Tooltip title="Dato registrado">
                          <LockRoundedIcon sx={{ color: "#94a3b8" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null
                  }}
                  sx={soloCorreoEditable ? readOnlySx : undefined}
                />

                <TextField
                  label="Dirección"
                  name="direccion"
                  fullWidth
                  margin="normal"
                  value={form.direccion}
                  onChange={onChangeForm}
                  InputProps={{
                    readOnly: soloCorreoEditable,
                    endAdornment: soloCorreoEditable ? (
                      <InputAdornment position="end">
                        <Tooltip title="Dato registrado">
                          <LockRoundedIcon sx={{ color: "#94a3b8" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null
                  }}
                  sx={soloCorreoEditable ? readOnlySx : undefined}
                />

                <TextField
                  label="Teléfono"
                  name="telefono"
                  fullWidth
                  margin="normal"
                  value={form.telefono}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d+]/g, "").slice(0, 20);
                    setForm((p) => ({ ...p, telefono: v }));
                  }}
                  InputProps={{
                    readOnly: soloCorreoEditable,
                    endAdornment: soloCorreoEditable ? (
                      <InputAdornment position="end">
                        <Tooltip title="Dato registrado">
                          <LockRoundedIcon sx={{ color: "#94a3b8" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null
                  }}
                  sx={soloCorreoEditable ? readOnlySx : undefined}
                />

                <TextField
                  label="Correo electrónico (aquí te enviamos tu clave)"
                  name="email"
                  type="email"
                  fullWidth
                  margin="normal"
                  value={form.email}
                  onChange={onChangeForm}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineRoundedIcon sx={{ color: BRAND }} />
                      </InputAdornment>
                    )
                  }}
                  helperText="Asegúrate de que esté correcto. Enviaremos una clave temporal para activar tu cuenta."
                />

                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={aceptaPoliticas}
                      onChange={(e) => setAceptaPoliticas(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: "#475569" }}>
                      Acepto las{" "}
                      <Link
                        href="/politicas-privacidad"
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                      >
                        políticas de privacidad
                      </Link>
                    </Typography>
                  }
                />

                <Button
                  type="submit"
                  fullWidth
                  disabled={!canSubmit}
                  sx={{
                    mt: 2,
                    height: 54,
                    borderRadius: 3,
                    fontWeight: 900,
                    textTransform: "none",
                    color: "#fff",
                    backgroundColor: BRAND,
                    boxShadow: "0 10px 20px rgba(0,51,102,0.25)",
                    "&:hover": {
                      backgroundColor: BRAND_HOVER,
                      boxShadow: "0 12px 24px rgba(0,51,102,0.30)"
                    },
                    "&:disabled": {
                      backgroundColor: "#A8B7C6",
                      color: "#F7FAFC"
                    }
                  }}
                >
                  {enviando ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                      <span>Creando tu cuenta</span>
                    </Stack>
                  ) : (
                    "Crear mi cuenta"
                  )}
                </Button>

                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: "#64748b", textAlign: "center", lineHeight: 1.6 }}
                >
                  Te enviaremos una <strong>clave temporal</strong> a tu correo para que ingreses y la
                  cambies en tu primer acceso.
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        <Typography variant="body2" sx={{ mt: 2, color: "#64748b", textAlign: "center" }}>
          ¿Ya tienes cuenta? Inicia sesión desde{" "}
          <Link href="/login" underline="always">
            aquí
          </Link>
          .
        </Typography>
      </Container>

      <Dialog
        open={successOpen}
        onClose={() => {}}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: { xs: 1, sm: 1.5 },
            width: "100%",
            maxWidth: 520,
            boxShadow: "0 18px 50px rgba(0,0,0,0.18)"
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CheckCircleRoundedIcon sx={{ color: BRAND, fontSize: 32 }} />
            <Box>
              <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: "1.15rem" }}>
                Registro exitoso
              </Typography>
              <Typography sx={{ color: "#475569", fontSize: "0.9rem" }}>
                Tu cuenta fue creada correctamente.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              border: "1px solid #E6EDF7",
              borderRadius: 3,
              p: 2,
              bgcolor: "#F7F9FC"
            }}
          >
            <Typography sx={{ color: "#0f172a", fontWeight: 800, mb: 0.5 }}>
              Enviamos tu clave temporal al correo:
            </Typography>
            <Typography sx={{ color: BRAND, fontWeight: 900, wordBreak: "break-all" }}>
              {correoConfirmado}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Typography sx={{ color: "#334155", lineHeight: 1.7 }}>
              Para continuar:
              <br />1. Revisa tu correo (y la carpeta de promociones/spam si no lo ves).
              <br />2. Inicia sesión con la clave temporal.
              <br />3. Cambia tu contraseña en el primer ingreso.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1.5 }}>
          <Button
            onClick={cerrarModalYLimpiar}
            variant="outlined"
            startIcon={<DoneRoundedIcon />}
            sx={{
              borderRadius: 3,
              fontWeight: 900,
              textTransform: "none",
              borderColor: "#CBD5E1",
              color: "#0f172a",
              "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F1F5F9" }
            }}
          >
            Entendido
          </Button>

          <Button
            onClick={irLogin}
            variant="contained"
            startIcon={<LoginRoundedIcon />}
            sx={{
              borderRadius: 3,
              fontWeight: 900,
              textTransform: "none",
              color: "#fff",
              backgroundColor: BRAND,
              boxShadow: "0 10px 20px rgba(0,51,102,0.25)",
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                boxShadow: "0 12px 24px rgba(0,51,102,0.30)"
              }
            }}
          >
            Ir al login
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Register;
